import React, {useEffect, useState} from 'react';
import axios from 'axios';
import {motion} from 'framer-motion';
import Hero from './Hero';
import Timeline from './Timeline';
import RSVPForm from './RSVPForm';
import ParallaxImage from './ParallaxImage';
import VenueRules from './VenueRules';
import ScrollCatAnimation from './ScrollCatAnimation';
import FAQSection from './FAQSection';
import {electionApi} from '../api/election';
import {Link} from 'react-router-dom';
import {useElectionWebSocket} from '../hooks/useElectionWebSocket';

const Invite = ({token}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [electionOpen, setElectionOpen] = useState(false);
    const [winnerName, setWinnerName] = useState('');

    const {lastMessage} = useElectionWebSocket(token);

    useEffect(() => {
        if (!lastMessage) return;
        switch (lastMessage.type) {
            case 'init':
                setElectionOpen(lastMessage.is_open);
                break;
            case 'election_open_changed':
                setElectionOpen(lastMessage.is_open);
                break;
            case 'winner_selected':
                setWinnerName(`${lastMessage.candidate.first_name} ${lastMessage.candidate.last_name}`);
                break;
            case 'election_reset':
                setElectionOpen(false);
                setWinnerName('');
                break;
            default:
                break;
        }
    }, [lastMessage]);

    useEffect(() => {
        if (token) {
            electionApi.getElectionStatus(token)
                .then(res => setElectionOpen(res.data.is_open))
                .catch(() => {
                });
            electionApi.getWinner()
                .then(res => setWinnerName(`${res.data.first_name} ${res.data.last_name}`))
                .catch(() => {
                });
        }
    }, [token]);

    const fetchInvite = () => {
        axios.get(`/api/invite/${token}`)
            .then((res) => {
                setData({...res.data, infoCards: res.data.infoCards || []});
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        fetchInvite();
    }, [token]);

    if (loading) return (
        <motion.div className="min-h-screen flex items-center justify-center" initial={{opacity: 0}}
                    animate={{opacity: 1}}>
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold-accent"></div>
        </motion.div>
    );

    if (!data) return (
        <motion.div className="min-h-screen flex items-center justify-center" initial={{opacity: 0}}
                    animate={{opacity: 1}}>
            <div className="text-center">
                <h1 className="text-2xl mb-4">Доступ запрещён</h1>
                <p className="text-gray-600">Пожалуйста, используйте корректную ссылку-приглашение</p>
            </div>
        </motion.div>
    );

    const {member, family, settings, effective_rsvp_deadline} = data;
    const fullName = `${member.first_name} ${member.patronymic ?? ''}`.trim();
    const isVoted = member.is_attending !== null;

    return (
        <div className="min-h-screen">
            {winnerName && (
                <div className="bg-gray-900 text-white py-2 overflow-hidden whitespace-nowrap">
                    <div className="animate-marquee whitespace-nowrap inline-block">
                        {[1, 2, 3].map(n => (
                            <span key={n} className="inline-block">
                                ✦ Следующий супружник: {winnerName} ✦ К Вам на свадьбу приедут Татьяна и Илья ✦
                                <span className="inline-block w-24"/>
                            </span>
                        ))}
                    </div>
                </div>
            )}
            {electionOpen && (
                <div
                    className="bg-gold-accent text-white py-3 px-4 text-center flex flex-col sm:flex-row items-center justify-center gap-3">
                    <span className="text-lg font-medium">Выборы следующего супружника</span>
                    <Link
                        to={`/vote?token=${token}`}
                        className="bg-white text-gold-accent px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition"
                    >
                        Проголосовать →
                    </Link>
                </div>
            )}
            <Hero
                name={fullName}
                gender={member.gender}
                weddingDate={settings.wedding_date}
                isVoted={isVoted}
                effectiveDeadline={effective_rsvp_deadline}
                menuToken={token}
                menuMember={member}
                menuFamily={family}
            />

            <Timeline settings={settings}/>
            <ParallaxImage src="/images/full-width-parallax.jpg" speed={1.2}
                           className="h-[400px] md:h-[500px] lg:h-[600px]">
                <div className="absolute inset-0 bg-black/10"/>
            </ParallaxImage>
            <VenueRules cards={data.infoCards}/>
            <ScrollCatAnimation/>
            <FAQSection/>

            <section id="rsvp-section" className="py-20 px-4 bg-gray-50">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-display mb-4">Надеемся увидеть вас</h2>
                        <p className="text-gray-600">Пожалуйста, подтвердите свое участие</p>
                    </div>
                    <div className="bg-white p-8 rounded-lg shadow-sm">
                        <RSVPForm
                            member={member}
                            family={family}
                            deadline={effective_rsvp_deadline}
                            token={token}
                            onSuccess={fetchInvite}
                        />
                    </div>
                </div>
            </section>

            <footer className="py-8 text-center text-gray-500 text-sm">
                <p>С любовью, Татьяна и Илья</p>
            </footer>
        </div>
    );
};

export default Invite;